import React from 'react';
import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay';

// helpers
import {
    isValidCreator,
    buildCreatorInfoString,
    buildCreatorRolesString,
} from './helpers/creatorHelpers';

// constants
const MIN_ITEM_COUNT = 2;
const ALLOWED_ATTRIBUTIONS = {
    CREATOR: ['BY', 'BY_AND_DOCUMENTED'],
};
const ALLOWED_ROLES = {
    ARTIST: ['ARTIST'],
    DESIGNER: ['DESIGNER'],
    BRAND: ['MANUFACTURER'],
};
const UNKNOWN_CREATOR = 'unknown';

function PdpSharedDetailCreator({ detail, item }) {
    const roles = ALLOWED_ROLES[detail];
    const attributions = ALLOWED_ATTRIBUTIONS[detail];
    const { creators = [] } = item;
    if (!creators.length) {
        return null;
    }
    // Creator Components
    const creatorComponents = creators.reduceRight((components, itemCreator, index) => {
        if (isValidCreator(itemCreator, attributions, roles)) {
            const { creator, rolesReferenceSet } = itemCreator;

            // Creator Name + Creator Info (e.g. 'Pablo Picasso (1881 - 1973, Spain)')
            const creatorName = creator.displayName || creator.name;
            const showCreatorInfo = detail === 'ARTIST';
            const creatorInfo = showCreatorInfo && buildCreatorInfoString(creator);
            const creatorString = creatorName + (creatorInfo ? ` ${creatorInfo}` : '');
            // Creator Url
            let creatorUrl = false;
            if (
                creator.urlTag &&
                creator.urlTag.toLowerCase() !== UNKNOWN_CREATOR &&
                detail !== 'BRAND' &&
                creator.publishedItemsCount >= MIN_ITEM_COUNT
            ) {
                creatorUrl = `/creators/${creator.urlTag}/${item.vertical}/`;
            }
            // Creator Roles
            const showCreatorRoles = detail === 'CREATOR';
            const creatorRoles = showCreatorRoles && buildCreatorRolesString(rolesReferenceSet);

            components.unshift(
                <span key={`${creatorName}-${index}`}>
                    {/* Creator Name */}
                    {creatorUrl ? <a href={creatorUrl}>{creatorString}</a> : creatorString}
                    {/* Creator Roles */}
                    {!!creatorRoles && ` ${creatorRoles}`}
                    {/* Delimiter */}
                    {!!components.length && ', '}
                </span>
            );
        }

        return components;
    }, []);

    return <span data-tn={`pdp-spec-${detail.toLowerCase()}`}>{creatorComponents}</span>;
}

PdpSharedDetailCreator.propTypes = {
    detail: PropTypes.oneOf(['CREATOR', 'ARTIST', 'BRAND', 'DESIGNER']).isRequired,
    item: PropTypes.object.isRequired,
};

PdpSharedDetailCreator.defaultProps = {
    preventLink: false,
    showCreatorInfo: false,
    hideCreatorRoles: false,
};

export default createFragmentContainer(PdpSharedDetailCreator, {
    item: graphql`
        fragment PdpSharedDetailCreator_item on Item {
            vertical
            isJewelry
            creators {
                attribution
                rolesReferenceSet {
                    enumName
                    displayName
                }
                creator {
                    name
                    displayName
                    urlTag
                    publishedItemsCount
                    dateOfBirth
                    dateOfDeath
                    countryOfOrigin
                    date
                }
            }
        }
    `,
});
